import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { store } from 'Reducers';
import { PhraseEndReason } from 'Types/EventTypes';
import logger from 'Utils/Logger';
import { getCurrentAuthToken } from 'Utils/Auth';
import { reportError } from 'Utils/reportError';
import { getPlatformInfo, getDeviceClass, getViewport } from 'Actions/events';
import { EventType, PhraseNoteError } from 'Types/EventTypes';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import type { MidiEvent } from './Phrase';
import type EventStream from './EventStream';
import { MAX_RESENDS, isDeviceOffline, isPermanentReject, resendDelayMs } from './eventSendPolicy';
import { claimKeepaliveChars, keepaliveCharsAvailable } from './keepaliveBudget';

// MUS-499: play-time per-note flush controller.
//
// Per-note rows are captured AS PLAYED (they already accumulate in the phrase's
// playData on the tick loop) and flushed asynchronously in batches, so a phrase the
// user stops on / half-plays / navigates away from no longer drops its notes.
//
// This is deliberately self-contained (its own transport, not the shared eventQueue)
// so it can (a) run its own 2s backstop cadence, and (b) use a keepalive/beacon send
// on teardown - without changing how any other event flushes.
//
// Triggers (wired by the caller):
//   - a 2s timer (crash/OOM backstop; most notes flush earlier on a boundary/pause)
//   - phrase boundary + pause          -> flush('boundary' | 'pause'), alive path
//   - visibility-hidden / app-inactive / pagehide -> flushOnTeardown(), keepalive path
//
// Idempotency: each flushed batch is one event carrying a client_event_id stamped
// ONCE when the batch is cut and stable across any resend (a network retry, or a
// teardown beacon re-sending an un-acked batch). The backend dedups on it via the
// shared event_dedup ledger (MUS-418). Complementarily, each note is serialized
// exactly once (the Flushed high-water mark on NoteEvent), so batches don't overlap
// under normal play - dedup only has to catch a true redelivery.
//
// Retry: a batch is retained and resent only while a resend can succeed - no
// response (network error), a 5xx, or a retryable 4xx. Any other 4xx is a permanent
// reject: the server would answer a resend the same way, so the POST's batches are
// dropped and reported once instead of being resent on every flush with each new
// batch piled on.
//
// MUS-636: a retained batch is not resent on every flush. Each one keeps its own
// count of failed sends and waits out a growing, jittered gap before its next try;
// after MAX_RESENDS failed resends it is dropped and reported once. A batch that is
// waiting does not hold back the flush: newer batches still go out on schedule. The
// stop flush sends every pending batch, waiting or not. A send that failed with no
// response while the device says it is offline waits like any other but does not
// count toward MAX_RESENDS, so notes played offline are kept until the network
// returns (bounded by MAX_RETAINED_BATCHES).

const FLUSH_INTERVAL_MS = 2000;

// MUS-636: the most batches kept at once. A batch holds the notes of one phrase
// played in one 2s window (a few KB), and a window with no notes cuts none, so
// this is roughly an hour of continuous play offline, a few MB. Past it the oldest
// batches are dropped and reported once until a send is accepted again.
const MAX_RETAINED_BATCHES = 1800;

// The slice of the events endpoint's partial-success summary this controller reads.
// A 422 carries it, with one failed[] entry per event the server could not persist.
// A 200 carries it too, with one skipped.invalid[] entry per event that failed
// validation and was not persisted.
type EventsPostSummary = {
  skipped?: { invalid?: Array<{ reason?: unknown }> };
  failed?: Array<{ reason?: unknown }>;
};

// Sight reading's transposition and mode conversion when a batch is cut. The
// caller flushes before either changes, so each batch carries the ones its
// notes were played under.
export type SightReadingTransforms = {
  transposeSemitones: number;
  modeConversion: ParallelModeConversion;
};

type LessonFlushContext = {
  surface?: 'lesson';
  playSessionId: string;
  levelNumber: number;
  tierNumber: number;
  errorRecognitionMode: string; // 'MIDI' | 'AUDIO'
  getTransforms?: () => SightReadingTransforms;
};

// The practice settings in force when a repertoire batch is cut. The caller
// flushes before any of them changes, so each batch carries the settings its
// notes were played under.
export type RepertoirePlaySettings = {
  loopActive: boolean;
  // Piece-relative whole-note timestamps, the note rows' tick units; null when
  // no loop is on.
  loopStartTick: number | null;
  loopEndTick: number | null;
  // Which hands are graded; null when neither is (nothing is graded then).
  handsGraded: 'LH' | 'RH' | 'HT' | null;
  transposeSemitones: number;
  modeConversion: string;
  // The Perfect Phrase streak target, null when the counter is off.
  perfectPhraseTarget: number | null;
};

type RepertoireFlushContext = {
  surface: 'repertoire';
  playSessionId: string;
  repertoireId: number;
  errorRecognitionMode: string; // 'MIDI' | 'AUDIO'
  getSettings: () => RepertoirePlaySettings;
};

type FlushContext = LessonFlushContext | RepertoireFlushContext;

// The notes of one phrase cut into one batch, and, for repertoire, the raw key
// presses on that phrase since its last batch.
type PlayTimeBatch = {
  phraseS3Id: string;
  notes: PhraseNoteError[];
  midiData?: MidiEvent[];
};

type GetPlayTimeBatches = (lookback?: number, includeHeld?: boolean) => PlayTimeBatch[];

// The flushes that come just before the stream's notes go away also take notes
// still held, which would otherwise never be sent: the session's last send
// ('stop'), the end of a loop pass ('pass-end'), a replay ('replay') and any other
// reset of the event stream ('reset': sight reading's tier end or session change,
// repertoire's renderer recovery).
const FLUSHES_THAT_TAKE_HELD_NOTES = new Set(['stop', 'pass-end', 'replay', 'reset']);

// One flushed play-time event: the wire payload plus its stable client_event_id,
// how many of its sends have failed (this sets the gap), how many of those count
// toward MAX_RESENDS (not the ones that failed offline), and the earliest time
// (epoch ms) it may be sent again (0 for a batch not sent yet).
type PendingBatch = {
  client_event_id: string;
  event: Record<string, unknown>;
  failedSends: number;
  countedResends: number;
  nextSendAt: number;
};

export class PlayTimeNoteFlushController {
  // Pulls the resolved-unflushed rows grouped per phrase from the active stream.
  private getBatches: GetPlayTimeBatches | null = null;
  // MUS-473: optional best-effort snapshot of the in-progress phrase at teardown.
  private getInterruptionSnapshot:
    | (() => { phraseS3Id?: string; position?: number } | null)
    | null = null;
  private context: FlushContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  // Un-acked flushed batches, keyed by client_event_id. Retained until a 2xx, a
  // permanent reject or the resend limit, then dropped. Re-sent on teardown / a later
  // flush with the SAME id so a redelivery dedups.
  private pending: Map<string, PendingBatch> = new Map();
  private sending = false;
  // Bumped by discardPending (sign-out). A send that started before it does not put
  // its batches back when it fails.
  private discardGeneration = 0;
  // The client_event_ids in a teardown keepalive that has not settled yet. A second
  // teardown signal (hidden, then pagehide) does not send them again.
  private inKeepalive: Set<string> = new Set();
  // MUS-636: set once MAX_RETAINED_BATCHES has dropped batches and been reported;
  // cleared when a send is accepted.
  private retainedLimitReported = false;
  // MUS-473: stable-per-INTERRUPTION-EPISODE id for the teardown SessionInterruption
  // event. One teardown fires flushOnTeardown() 2-3x (visibilitychange->hidden,
  // pagehide, and appStateChange->inactive on mobile); those share this id so the
  // MUS-418 backend dedup collapses them to a single interruption row. A foreground-
  // return (resetInterruptionEpisode) clears it, so a LATER background within the same
  // play session mints a fresh id and records as its OWN distinct interruption at its
  // own phrase - each genuine leave-point is preserved, rather than first-write-wins
  // freezing the whole session at the earliest one. Null until the first teardown of
  // an episode mints one; reset on foreground-return, session (re)configure, and stop.
  private interruptionClientEventId: string | null = null;

  private get eventsUrl(): string {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v1/events`;
  }

  private get enabled(): boolean {
    return process.env.REACT_APP_SEND_EVENTS === 'true';
  }

  // Called when a play session starts. Idempotent: reconfiguring restarts the timer.
  configure(
    getBatches: GetPlayTimeBatches,
    context: FlushContext,
    getInterruptionSnapshot?: () => { phraseS3Id?: string; position?: number } | null,
  ) {
    this.getBatches = getBatches;
    this.context = context;
    this.getInterruptionSnapshot = getInterruptionSnapshot ?? null;
    // A new session starts a fresh interruption episode.
    this.interruptionClientEventId = null;
    this.startTimer();
  }

  private startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => {
      this.flush('timer').catch((err) => logger.warn('[play-time-flush] timer flush failed', err));
    }, FLUSH_INTERVAL_MS);
  }

  private stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Called when a play session ends. Does a final alive flush, then detaches. Pending
  // batches survive the detach so a straggler retry (or teardown) can still send them.
  // The final batches are cut synchronously (flush captures before its first await),
  // so the controller detaches before the send: a configure() for the next session
  // made while the send is in flight is not undone when it finishes.
  async stop() {
    this.stopTimer();
    const finalFlush = this.flush('stop');
    this.getBatches = null;
    this.context = null;
    this.getInterruptionSnapshot = null;
    this.interruptionClientEventId = null;
    await finalFlush.catch((err) => logger.warn('[play-time-flush] stop flush failed', err));
  }

  // On sign-out: drop every batch not yet accepted and detach from the stream, so
  // nothing played or retained under this account is sent later under whichever
  // account signs in next. The play screen's own stop() then finds nothing to send;
  // the next configure() attaches again.
  discardPending() {
    this.stopTimer();
    this.pending.clear();
    this.discardGeneration += 1;
    this.retainedLimitReported = false;
    this.getBatches = null;
    this.context = null;
    this.getInterruptionSnapshot = null;
    this.interruptionClientEventId = null;
  }

  // The input mode can change mid-session (the input setup); batches cut after the
  // change carry the new one.
  setErrorRecognitionMode(errorRecognitionMode: string) {
    if (this.context) this.context = { ...this.context, errorRecognitionMode };
  }

  // MUS-473: called when the app returns to the foreground (tab visible / app active).
  // Ends the current interruption episode so the NEXT teardown mints a fresh id. A
  // single teardown's 2-3 duplicate fires still share one id (they happen before any
  // return); a genuine background-then-return-then-background sequence records as two
  // distinct interruptions, each at its own in-progress phrase.
  resetInterruptionEpisode() {
    this.interruptionClientEventId = null;
  }

  private buildBase(): Record<string, unknown> {
    const sessionId = store.getState()?.eventReducer?.sessionId ?? '';
    return {
      datetime: new Date().toISOString(),
      analytics_session_id: sessionId,
      platform: getPlatformInfo(),
      device_class: getDeviceClass(),
      ...getViewport(),
    };
  }

  // Pull unflushed rows, cut one batch per phrase (each with its own client_event_id),
  // and add them to the pending set. Returns whether anything new was captured.
  // includeHeld (the stop and teardown sends) also takes notes whose key is still
  // down, which would otherwise never be sent.
  private capture(lookback?: number, includeHeld: boolean = false): boolean {
    if (!this.getBatches || !this.context) return false;
    const groups = this.getBatches(lookback, includeHeld);
    if (!groups.length) return false;
    const base = this.buildBase();
    for (const group of groups) {
      const clientEventId = uuidv4();
      this.pending.set(clientEventId, {
        client_event_id: clientEventId,
        event: {
          ...base,
          client_event_id: clientEventId,
          ...playTimeEventFields(this.context, group),
        },
        failedSends: 0,
        countedResends: 0,
        nextSendAt: 0,
      });
    }
    return true;
  }

  // MUS-378 RUM path: one error per permanently rejected POST, under the same
  // 'events.post' tag the shared queue reports its rejected entries with.
  private reportDroppedBatches(dropped: PendingBatch[], httpStatus: number, err: unknown) {
    const summary = axios.isAxiosError(err)
      ? (err.response?.data as EventsPostSummary | undefined)
      : undefined;
    const failed = summary?.failed ?? [];
    const firstReason = failed[0]?.reason;
    const detail = {
      httpStatus,
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      failedCount: failed.length,
      firstFailureReason: typeof firstReason === 'string' ? firstReason.slice(0, 120) : undefined,
    };
    reportError(
      new Error(`play-time notes POST rejected, batches dropped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // MUS-636: one error per failed POST that used up the last resend of one or more
  // batches. Counts only: no failure reason (a database error's text can carry the
  // values it was writing) and no note data.
  private reportResendsExhausted(dropped: PendingBatch[], httpStatus: number | undefined) {
    const detail = {
      httpStatus,
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      resends: MAX_RESENDS,
    };
    reportError(
      new Error(`play-time notes resend limit reached, batches dropped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // MUS-636: after a failed send a resend could fix, each batch in the POST counts
  // one more failed send, and one more toward MAX_RESENDS unless the send got no
  // response while the device is offline. A batch past MAX_RESENDS is dropped and
  // reported; the rest wait out their backoff. Map.set keeps each batch's place, so
  // resends go out in the order the batches were cut.
  private scheduleResends(sent: PendingBatch[], httpStatus: number | undefined, err: unknown) {
    const now = Date.now();
    const countsTowardLimit = !(httpStatus === undefined && isDeviceOffline());
    const counted = sent.map((b) => ({
      ...b,
      failedSends: b.failedSends + 1,
      countedResends: b.countedResends + (countsTowardLimit ? 1 : 0),
    }));
    const exhausted = counted.filter((b) => b.countedResends > MAX_RESENDS);
    counted
      .filter((b) => b.countedResends <= MAX_RESENDS)
      .forEach((b) =>
        this.pending.set(b.client_event_id, {
          ...b,
          nextSendAt: now + resendDelayMs(b.failedSends),
        }),
      );
    exhausted.forEach((b) => this.pending.delete(b.client_event_id));
    if (exhausted.length > 0) this.reportResendsExhausted(exhausted, httpStatus);
    logger.warn('[play-time-flush] alive flush failed; retaining for retry', err);
  }

  // MUS-636: keep at most MAX_RETAINED_BATCHES, dropping the oldest. Reported once
  // (counts only) until a send is accepted again.
  private dropOldestPastRetainedLimit() {
    const excess = this.pending.size - MAX_RETAINED_BATCHES;
    if (excess <= 0) return;
    const dropped = [...this.pending.values()].slice(0, excess);
    dropped.forEach((b) => this.pending.delete(b.client_event_id));
    if (this.retainedLimitReported) return;
    this.retainedLimitReported = true;
    const detail = {
      droppedBatches: dropped.length,
      droppedNotes: dropped.reduce(
        (total, b) => total + (Array.isArray(b.event.notes) ? b.event.notes.length : 0),
        0,
      ),
      limit: MAX_RETAINED_BATCHES,
    };
    reportError(
      new Error(
        `play-time notes retained batch limit reached, oldest dropped ${JSON.stringify(detail)}`,
      ),
      'events.post',
    );
  }

  // One error per accepted POST whose summary lists events skipped as invalid. The
  // server did not persist them and would skip a resend the same way, so they stay
  // acked; without this report the loss is silent on both sides.
  private reportInvalidSkips(
    summary: EventsPostSummary | undefined,
    httpStatus: number | undefined,
    sentBatches: number,
  ) {
    const invalid = summary?.skipped?.invalid ?? [];
    if (invalid.length === 0) return;
    const firstReason = invalid[0]?.reason;
    const detail = {
      httpStatus,
      sentBatches,
      invalidCount: invalid.length,
      firstInvalidReason:
        firstReason === undefined ? undefined : JSON.stringify(firstReason).slice(0, 120),
    };
    reportError(
      new Error(`play-time notes POST accepted, invalid batches skipped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // Alive-path flush (timer / boundary / pause / stop): capture, then POST the
  // pending batches that are due (new ones, and retained ones whose backoff has run
  // out) over the authenticated axios path (Bearer attached by the interceptor). On
  // 2xx, drop the batches that were sent, reporting any the server skipped as
  // invalid. On a permanent reject, drop and report them. Any other failure keeps
  // them for a later flush, after a backoff, up to MAX_RESENDS resends. The stop
  // flush is the session's last alive send: it sends every pending batch, waiting
  // or not, and a failure still counts toward MAX_RESENDS.
  async flush(reason: string): Promise<void> {
    if (!this.enabled) return;
    this.capture(undefined, FLUSHES_THAT_TAKE_HELD_NOTES.has(reason));
    if (this.sending) return;
    this.dropOldestPastRetainedLimit();
    const now = Date.now();
    const ignoreBackoff = reason === 'stop';
    const inFlight = [...this.pending.values()].filter((b) => ignoreBackoff || b.nextSendAt <= now);
    if (inFlight.length === 0) return;
    this.sending = true;
    const generation = this.discardGeneration;
    try {
      const response = await axios.post<EventsPostSummary>(
        this.eventsUrl,
        inFlight.map((b) => b.event),
      );
      for (const b of inFlight) this.pending.delete(b.client_event_id);
      this.retainedLimitReported = false;
      this.reportInvalidSkips(response?.data, response?.status, inFlight.length);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (generation !== this.discardGeneration) {
        // Signed out while this POST was out: its batches were discarded.
        logger.warn('[play-time-flush] alive flush failed after sign-out; not retained', err);
      } else if (status !== undefined && isPermanentReject(status)) {
        // The server would answer a resend the same way, and on the events
        // endpoint's own 422 it has already persisted every event it could. Nothing
        // in this POST is worth keeping: a retained batch would be resent on every
        // flush for the rest of the page's life.
        for (const b of inFlight) this.pending.delete(b.client_event_id);
        this.reportDroppedBatches(inFlight, status, err);
      } else {
        // Keep the batches pending; a later flush (or teardown) resends them. The
        // stable client_event_id means the resend dedups if the first actually landed.
        this.scheduleResends(inFlight, status, err);
      }
    } finally {
      this.sending = false;
    }
  }

  // Teardown-path flush (page hidden / app inactive / pagehide / unload). Captures a
  // wider lookback for safety, then sends all pending batches with a transport that
  // survives teardown: keepalive fetch (carries the Authorization header, so it hits
  // the same auth-guarded endpoint), falling back to sendBeacon only where fetch
  // keepalive is unavailable. Best-effort and non-blocking; pending is NOT cleared
  // (the page is going away - if it survives, the next alive flush resends and the
  // backend dedups). Events still in an earlier teardown keepalive are not sent
  // again. The body claims its size of the page's keepalive allowance (see
  // keepaliveBudget) before the event queue's teardown send, which takes what is left.
  async flushOnTeardown(): Promise<void> {
    if (!this.enabled) return;
    this.capture(12, true);
    // MUS-473: a page teardown during play is an interruption. Emit one
    // SessionInterruptionEvent (its own client_event_id) alongside any pending
    // note batches, on the same keepalive/beacon path. Sent even when no notes
    // are pending, so a mid-phrase reload after the last flush still records it.
    const outgoing: Record<string, unknown>[] = [];
    if (this.context) {
      const snap = this.getInterruptionSnapshot?.() ?? null;
      // Mint ONCE per interruption episode (see the field doc), then reuse across the
      // 2-3 duplicate teardown fires so they dedup to one row. resetInterruptionEpisode
      // (foreground-return) / configure (new session) / stop clear it.
      this.interruptionClientEventId ??= uuidv4();
      const interruptionClientEventId = this.interruptionClientEventId;
      outgoing.push({
        ...this.buildBase(),
        client_event_id: interruptionClientEventId,
        ...interruptionEventFields(this.context),
        phrase_s3_id: snap?.phraseS3Id,
        position: snap?.position,
        // MUS-646: the reason the phrase ended; a teardown is not final (the
        // player can come back and finish the phrase).
        reason: PhraseEndReason.Teardown,
      });
    }
    for (const b of this.pending.values()) outgoing.push(b.event);
    const unsent = outgoing.filter((event) => !this.inKeepalive.has(String(event.client_event_id)));
    if (unsent.length === 0) return;
    const body = JSON.stringify(unsent);

    const supportsKeepalive = typeof fetch === 'function';
    if (supportsKeepalive) {
      // Claimed before waiting for the token, so the event queue's teardown send,
      // called right after this one, sees these bytes as taken. A body larger than
      // what is left would be refused anyway, so it claims nothing.
      const ids = unsent.map((event) => String(event.client_event_id));
      ids.forEach((id) => this.inKeepalive.add(id));
      const releaseClaim =
        body.length <= keepaliveCharsAvailable() ? claimKeepaliveChars(body.length) : () => {};
      try {
        let token: string | undefined;
        try {
          token = await getCurrentAuthToken();
        } catch {
          token = undefined;
        }
        await fetch(this.eventsUrl, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body,
        });
        return;
      } catch (err) {
        logger.warn('[play-time-flush] keepalive fetch failed', err);
      } finally {
        releaseClaim();
        ids.forEach((id) => this.inKeepalive.delete(id));
      }
    }

    // Last resort: sendBeacon cannot set an Authorization header, so this only lands
    // if the endpoint can accept it; used solely where keepalive fetch is unavailable.
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(this.eventsUrl, new Blob([body], { type: 'application/json' }));
      }
    } catch (err) {
      logger.warn('[play-time-flush] sendBeacon fallback failed', err);
    }
  }
}

// The event a batch is sent as: lessons send PlayTimeNotesEvent with the level
// and tier (and, when the caller supplies them, sight reading's transposition
// and mode conversion); repertoire sends RepPlayTimeNotesEvent with the piece, the settings
// in force (per note where they can change while a phrase is played) and the
// batch's raw key presses.
const playTimeEventFields = (
  context: FlushContext,
  group: PlayTimeBatch,
): Record<string, unknown> => {
  if (context.surface !== 'repertoire') {
    const transforms = context.getTransforms?.();
    return {
      event_type: EventType.PlayTimeNotesEvent,
      phrase_s3_id: group.phraseS3Id,
      level_number: context.levelNumber,
      tier_number: context.tierNumber,
      play_session_id: context.playSessionId,
      error_recognition_mode: context.errorRecognitionMode,
      notes: group.notes,
      ...(transforms && {
        transpose_semitones: transforms.transposeSemitones,
        mode_conversion: transforms.modeConversion,
      }),
    };
  }
  const settings = context.getSettings();
  const noteSettings = {
    loop_active: settings.loopActive,
    loop_start_tick: settings.loopActive ? settings.loopStartTick : null,
    loop_end_tick: settings.loopActive ? settings.loopEndTick : null,
    hands_graded: settings.handsGraded,
  };
  return {
    event_type: EventType.RepPlayTimeNotesEvent,
    phrase_s3_id: group.phraseS3Id,
    repertoire_id: context.repertoireId,
    play_session_id: context.playSessionId,
    error_recognition_mode: context.errorRecognitionMode,
    notes: group.notes.map((note) => ({ ...note, ...noteSettings })),
    midi_data: group.midiData ?? [],
    transpose_semitones: settings.transposeSemitones,
    mode_conversion: settings.modeConversion,
    perfect_phrase_target: settings.perfectPhraseTarget,
  };
};

// The teardown interruption: SessionInterruptionEvent for a lesson, whose play
// session is a lesson session; RepertoireInterruptionEvent for a piece.
const interruptionEventFields = (context: FlushContext): Record<string, unknown> =>
  context.surface === 'repertoire'
    ? {
        event_type: EventType.RepertoireInterruptionEvent,
        play_session_id: context.playSessionId,
        repertoire_id: context.repertoireId,
      }
    : {
        event_type: EventType.SessionInterruptionEvent,
        play_session_id: context.playSessionId,
        level_number: context.levelNumber,
        tier_number: context.tierNumber,
      };

// MUS-473: the teardown interruption snapshot the play screen configures. It
// reads the phrase in progress (and the position inside it) off the live event
// stream. While the stream holds no phrase - before the first is laid out, or
// after a session or tier reset empties it - the event carries no phrase rather
// than a stale one.
export const phraseInProgressSnapshot =
  (stream: Pick<EventStream, 'getPhraseInProgress'>) =>
  (): { phraseS3Id?: string; position?: number } | null =>
    stream.getPhraseInProgress() ?? null;

// Singleton - one active play session at a time.
export const playTimeNoteFlushController = new PlayTimeNoteFlushController();
